/**
 * Admin Token Gate Page
 * Second step: user enters and verifies the admin token
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, TextField, Button, Typography, Alert } from '@mui/material';
import { useAdminAuthStore } from '@/stores/adminAuthStore';
import { platformAdminApi } from '@/api/client';

export function AdminTokenGatePage() {
  const navigate = useNavigate();
  const setAdminToken = useAdminAuthStore((s) => s.setAdminToken);
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Temporarily set token in store to call API
      setAdminToken(token);

      // Verify the token
      await platformAdminApi.verifyAdminToken();

      // Token is valid, keep it in store and navigate to dashboard
      navigate('/');
    } catch (err) {
      setAdminToken(''); // Clear invalid token
      const message = err instanceof Error ? err.message : 'Invalid admin token';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 3,
        }}
      >
        <Typography variant="h4" sx={{ textAlign: 'center' }}>
          Admin Token
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
          Enter your admin token to access the admin panel.
        </Typography>

        {error && <Alert severity="error">{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Admin Token"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              fullWidth
              disabled={loading}
              placeholder="Enter your admin token"
            />
            <Button
              variant="contained"
              color="primary"
              type="submit"
              fullWidth
              disabled={loading || !token.trim()}
            >
              {loading ? 'Verifying...' : 'Enter'}
            </Button>
          </Box>
        </form>
      </Box>
    </Container>
  );
}
